<template>
  <div class="webpage-page">
    <DashboardPageHeader
      title="Customer sign-up page"
      subtitle="Edit the page customers see when they join your loyalty scheme."
    >
      <template v-if="!loading && canEdit" #actions>
        <v-btn
          variant="text"
          color="primary"
          :disabled="!isDirty || saving"
          @click="onDiscard"
        >
          Discard changes
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          :loading="saving"
          :disabled="!isDirty || saving"
          @click="onSave"
        >
          Save changes
        </v-btn>
      </template>
    </DashboardPageHeader>

    <div v-if="loading" class="webpage-loading">
      <BrandedLoader text="Loading your page…" spinner-color="primary" />
    </div>

    <div v-else-if="loadError" class="webpage-empty" role="alert">
      <v-icon class="webpage-empty-icon">mdi-alert-circle-outline</v-icon>
      <h2>We couldn't load this business</h2>
      <p>{{ loadError }}</p>
      <v-btn
        class="mt-3"
        color="primary"
        variant="flat"
        prepend-icon="mdi-refresh"
        @click="loadData"
      >
        Try again
      </v-btn>
    </div>

    <template v-else>
      <div v-if="!canEdit" class="webpage-readonly-banner" role="status">
        <v-icon class="webpage-readonly-icon">mdi-lock-outline</v-icon>
        <div>
          <strong>You're viewing in read-only mode.</strong>
          Only the owner or an administrator can change your sign-up page.
        </div>
      </div>

      <div class="webpage-status-row">
        <div v-if="saveError" class="webpage-status webpage-status--error" role="alert">
          <v-icon class="webpage-status-icon">mdi-alert-circle</v-icon>
          {{ saveError }}
        </div>
        <div
          v-else-if="saveSuccess"
          class="webpage-status webpage-status--success"
          role="status"
        >
          <v-icon class="webpage-status-icon">mdi-check-circle</v-icon>
          Sign-up page saved.
        </div>
        <div
          v-else-if="isDirty && canEdit"
          class="webpage-status webpage-status--dirty"
          role="status"
        >
          You have unsaved changes.
        </div>
      </div>

      <section class="webpage-share-card">
        <div class="webpage-share-text">
          <span class="webpage-share-label">Sharable customer sign-up link</span>
          <span class="webpage-share-url">{{ signupUrl }}</span>
        </div>
        <v-btn
          variant="outlined"
          color="primary"
          density="comfortable"
          :prepend-icon="copyIcon"
          @click="onCopyLink"
        >
          {{ copyLabel }}
        </v-btn>
      </section>

      <div class="webpage-grid">
        <section class="webpage-editor-pane">
          <div class="webpage-editor-wrap">
            <div class="webpage-field-block">
              <label class="webpage-block-label" for="webpage-title">Title</label>
              <p class="webpage-block-hint">
                The headline customers see when they join - keep it short and warm.
              </p>
              <v-text-field
                id="webpage-title"
                :model-value="form.title"
                variant="outlined"
                density="comfortable"
                hide-details
                placeholder="Welcome to our rewards programme!"
                :readonly="!canEdit"
                @update:model-value="onFieldUpdate('title', $event)"
              />
            </div>

            <div class="webpage-field-block">
              <label class="webpage-block-label" for="webpage-subtitle">Subtitle</label>
              <p class="webpage-block-hint">
                One sentence explaining the reward (e.g. "Get every 10th coffee free.").
              </p>
              <v-textarea
                id="webpage-subtitle"
                :model-value="form.subtitle"
                variant="outlined"
                density="comfortable"
                hide-details
                rows="3"
                auto-grow
                placeholder="Join our rewards scheme and get every 10th drink free."
                :readonly="!canEdit"
                @update:model-value="onFieldUpdate('subtitle', $event)"
              />
            </div>

            <p class="webpage-meta-hint">
              Your sign-up page uses the same logo and colours as your loyalty
              card.
            </p>
          </div>
        </section>

        <aside class="webpage-preview-pane">
          <div class="webpage-preview-sticky">
            <WebDisplayPreview
              :title="form.title"
              :subtitle="form.subtitle"
              :logo-url="logoUrl"
              :background-color="backgroundHex"
              :text-color="textHex"
            />
            <p class="webpage-preview-caption">
              Live preview - this is what new customers see when they tap your link.
            </p>
          </div>
        </aside>
      </div>

      <div
        v-if="isDirty && canEdit"
        class="webpage-sticky-actions"
        role="region"
        aria-label="Unsaved changes"
      >
        <span class="webpage-sticky-label">You have unsaved changes</span>
        <div class="webpage-sticky-buttons">
          <v-btn
            variant="text"
            color="primary"
            :disabled="saving"
            @click="onDiscard"
          >
            Discard
          </v-btn>
          <v-btn
            variant="flat"
            color="primary"
            :loading="saving"
            :disabled="saving"
            @click="onSave"
          >
            Save changes
          </v-btn>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { computed, inject, onMounted, ref, watch } from "vue";
import WebDisplayPreview from "@/components/Organisms/WebDisplayPreview.vue";
import DashboardPageHeader from "@/components/Molecules/DashboardPageHeader.vue";
import BrandedLoader from "@/components/Molecules/BrandedLoader.vue";
import {
  getBusinessSnapshot,
  updateWebDisplayData,
  getCurrentUserRole,
} from "@/firebase-business.js";
import { getLogoImageUrl } from "@/firebase-storage.js";
import { authStore } from "@/stores/authStore.js";
import { hexToRgb, rgbToHex } from "@/utils/colorUtils.js";
import { track } from "@/analytics.js";

const DEFAULT_FORM = { title: "", subtitle: "" };

export default {
  name: "DashboardWebPageComponent",
  components: { WebDisplayPreview, DashboardPageHeader, BrandedLoader },
  setup() {
    const selectedBusinessId = inject("selectedBusinessId");

    const loading = ref(true);
    const loadError = ref("");
    const saving = ref(false);
    const saveError = ref("");
    const saveSuccess = ref(false);
    const form = ref({ ...DEFAULT_FORM });
    const originalForm = ref({ ...DEFAULT_FORM });
    const role = ref("Employee");
    const logoUrl = ref("");
    const backgroundHex = ref("#ffffff");
    const textHex = ref("#000000");
    const copyState = ref("idle");

    const canEdit = computed(
      () => role.value === "Owner" || role.value === "Administrator"
    );

    const isDirty = computed(
      () =>
        form.value.title !== originalForm.value.title ||
        form.value.subtitle !== originalForm.value.subtitle
    );

    const signupUrl = computed(() => {
      const id = selectedBusinessId?.value || "";
      return id
        ? `https://lulurewards.co.uk/business/${id}`
        : "https://lulurewards.co.uk/business/";
    });

    const copyIcon = computed(() =>
      copyState.value === "copied" ? "mdi-check" : "mdi-content-copy"
    );
    const copyLabel = computed(() =>
      copyState.value === "copied" ? "Copied" : "Copy link"
    );

    const loadData = async () => {
      const id = selectedBusinessId?.value;
      if (!id) {
        loading.value = false;
        return;
      }
      loading.value = true;
      loadError.value = "";
      saveError.value = "";
      saveSuccess.value = false;
      try {
        const [snapshot, resolvedRole] = await Promise.all([
          getBusinessSnapshot(id),
          getCurrentUserRole(id, authStore.user?.email || ""),
        ]);
        role.value = resolvedRole || "Employee";

        const web = (snapshot && snapshot.webDisplayData) || {};
        const card = (snapshot && snapshot.rewardsCardData) || {};
        const next = {
          title: String(web.title || ""),
          subtitle: String(web.subtitle || ""),
        };
        form.value = { ...next };
        originalForm.value = { ...next };
        backgroundHex.value =
          rgbToHex(web.backgroundColor || card.backgroundColor) || "#ffffff";
        textHex.value =
          rgbToHex(web.textColor || card.foregroundColor) || "#000000";

        try {
          const url = await getLogoImageUrl(id);
          logoUrl.value = url || "";
        } catch (e) {
          logoUrl.value = "";
        }
        track("dashboard_webpage_viewed", { business_id: id });
      } catch (e) {
        loadError.value =
          "Something went wrong loading your sign-up page. Please refresh and try again.";
      } finally {
        loading.value = false;
      }
    };

    onMounted(loadData);
    watch(() => selectedBusinessId?.value, loadData);

    const onFieldUpdate = (key, value) => {
      form.value = { ...form.value, [key]: value };
      saveSuccess.value = false;
    };

    const onDiscard = () => {
      form.value = { ...originalForm.value };
      saveError.value = "";
      saveSuccess.value = false;
    };

    const onSave = async () => {
      if (!canEdit.value) {
        saveError.value =
          "Only the owner or an administrator can change your sign-up page.";
        return;
      }
      const id = selectedBusinessId?.value;
      if (!id) return;
      saving.value = true;
      saveError.value = "";
      saveSuccess.value = false;
      try {
        const partial = {
          title: form.value.title || "",
          subtitle: form.value.subtitle || "",
          backgroundColor: hexToRgb(backgroundHex.value),
          textColor: hexToRgb(textHex.value),
        };
        await updateWebDisplayData(id, partial);
        originalForm.value = { ...form.value };
        saveSuccess.value = true;
        track("dashboard_webpage_saved", { business_id: id });
      } catch (e) {
        saveError.value =
          "We couldn't save your sign-up page. Please try again in a moment.";
      } finally {
        saving.value = false;
      }
    };

    const onCopyLink = async () => {
      const value = signupUrl.value;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(value);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = value;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        copyState.value = "copied";
        setTimeout(() => {
          copyState.value = "idle";
        }, 2000);
        track("dashboard_webpage_link_copied", { business_id: selectedBusinessId?.value });
      } catch (e) {
        copyState.value = "idle";
      }
    };

    return {
      loading,
      loadError,
      saving,
      saveError,
      saveSuccess,
      form,
      role,
      canEdit,
      isDirty,
      signupUrl,
      logoUrl,
      backgroundHex,
      textHex,
      copyIcon,
      copyLabel,
      loadData,
      onFieldUpdate,
      onDiscard,
      onSave,
      onCopyLink,
    };
  },
};
</script>

<style scoped>
.webpage-page :deep(.v-btn) {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  border-radius: 999px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* Primary green pill - matches the header "Get started" button. */
.webpage-page :deep(.v-btn--variant-flat.text-primary),
.webpage-page :deep(.v-btn.bg-primary) {
  background: rgb(0, 109, 62) !important;
  color: #ffffff !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
}

.webpage-page :deep(.v-btn--variant-flat.text-primary:hover),
.webpage-page :deep(.v-btn.bg-primary:hover) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
  transform: translateY(-1px);
}

/* Secondary outlined buttons (Copy link) - same hover pop, lighter shadow. */
.webpage-page :deep(.v-btn--variant-outlined.text-primary) {
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.1) !important;
}

.webpage-page :deep(.v-btn--variant-outlined.text-primary:hover) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.2) !important;
  transform: translateY(-1px);
}

/* Ghost / text buttons (Discard changes) - pop on hover without a fill. */
.webpage-page :deep(.v-btn--variant-text.text-primary:hover) {
  transform: translateY(-1px);
}

/* Disabled state must not lift. */
.webpage-page :deep(.v-btn--disabled:hover) {
  transform: none !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.1) !important;
}

.webpage-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.webpage-loading {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}

.webpage-empty {
  text-align: center;
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 48px 24px;
}

.webpage-empty-icon {
  color: var(--lulu-text-subtle);
  font-size: 48px !important;
  margin-bottom: 12px;
}

.webpage-readonly-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--lulu-surface);
  border: 1px solid var(--lulu-border);
  border-radius: var(--lulu-radius-card);
  padding: 12px 16px;
  color: var(--lulu-text-muted);
  font-size: 13px;
  line-height: 1.5;
}

.webpage-readonly-icon {
  color: var(--lulu-text-muted) !important;
}

.webpage-status-row {
  min-height: 24px;
}

.webpage-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
}

.webpage-status--success {
  background: var(--lulu-primary-soft);
  color: var(--lulu-primary);
}

.webpage-status--error {
  background: var(--lulu-danger-soft);
  color: var(--lulu-danger);
}

.webpage-status--dirty {
  background: var(--lulu-warning-soft);
  color: var(--lulu-warning-text);
}

.webpage-status-icon {
  font-size: 16px !important;
}

.webpage-share-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 16px 20px;
}

.webpage-share-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.webpage-share-label {
  font-size: 12px;
  color: var(--lulu-text-muted);
  text-transform: none;
  letter-spacing: 0.02em;
  font-weight: 600;
}

.webpage-share-url {
  font-size: 15px;
  font-weight: 700;
  color: var(--lulu-text-strong);
  word-break: break-all;
}

.webpage-grid {
  display: grid;
  gap: 24px;
  grid-template-columns: 1fr;
}

@media (min-width: 960px) {
  .webpage-grid {
    grid-template-columns: minmax(0, 1fr) 320px;
  }
}

.webpage-editor-pane {
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 24px;
  min-width: 0;
}

.webpage-editor-wrap {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.webpage-field-block {
  display: flex;
  flex-direction: column;
}

.webpage-block-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--lulu-primary);
  margin-bottom: 4px;
}

.webpage-block-hint {
  font-size: 12px;
  color: var(--lulu-text-muted);
  margin: 0 0 10px;
  line-height: 1.5;
}

.webpage-meta-hint {
  margin: 0;
  font-size: 12px;
  color: var(--lulu-text-muted);
  line-height: 1.5;
}

.webpage-meta-hint :deep(a) {
  color: var(--lulu-link);
  font-weight: 600;
  text-decoration: none;
}

.webpage-meta-hint :deep(a:hover) {
  text-decoration: underline;
}

.webpage-preview-pane {
  display: flex;
}

.webpage-preview-sticky {
  background: var(--lulu-surface);
  border-radius: var(--lulu-radius-card);
  box-shadow: var(--lulu-card-shadow);
  padding: 24px 16px;
  width: 100%;
  position: relative;
}

@media (min-width: 960px) {
  .webpage-preview-sticky {
    position: sticky;
    top: 88px;
  }
}

/* Mobile / narrow viewports: show the live preview ABOVE the editor so
   users see their changes as they type. DOM order stays semantic; CSS
   `order` swaps the visual order only. */
@media (max-width: 959px) {
  .webpage-preview-pane {
    order: 1;
  }
  .webpage-editor-pane {
    order: 2;
  }
}

.webpage-preview-caption {
  margin: 14px 0 0;
  font-size: 12px;
  color: var(--lulu-text-muted);
  text-align: center;
  line-height: 1.5;
}

/* Sticky save bar - visible while the form is dirty so the primary action
   stays in reach even after the user scrolls past the header. */
.webpage-sticky-actions {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  background: var(--lulu-surface);
  border-top: 1px solid var(--lulu-border);
  border-radius: var(--lulu-radius-card) var(--lulu-radius-card) 0 0;
  box-shadow: 0 -4px 16px rgba(0, 109, 62, 0.08);
  padding: 12px 20px;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
  z-index: 4;
}

.webpage-sticky-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--lulu-text-strong);
}

.webpage-sticky-buttons {
  display: flex;
  gap: 8px;
}
</style>
